import { BookingBusinessId, BookingServiceId } from '../types/booking';
import { FormField } from '../types/rest';

// Define the structure for GTM data layer
interface GTMDataLayer {
	[key: string]: unknown;
}

// Extend Window interface to include dataLayer
declare global {
	interface Window {
		dataLayer: GTMDataLayer[];
	}
}

type EventData = Record<string, unknown>;

export const useAnalytics = (bookingBusinessId: BookingBusinessId, bookingServiceId: BookingServiceId) => {
	const pushTagManagerEvent = (eventName: string, eventData: EventData = {}): void => {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: eventName,
			booking: {
				...eventData,
				businessId: bookingBusinessId,
				serviceId: bookingServiceId,
			},
		});
	};

	const gtmInitializeBookingData = (totalFormSteps: number): void => {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			booking: {
				businessId: bookingBusinessId,
				serviceId: bookingServiceId,
				totalFormSteps,
			},
		});
	};

	const gtmEventEditAppointment = (): void => {
		pushTagManagerEvent('bookingEditAppointment');
	};

	const gtmEventChangeAppointment = (): void => {
		pushTagManagerEvent('bookingChangeAppointment');
	};

	const gtmEventSearchTimezone = (searchTerm: string): void => {
		pushTagManagerEvent('bookingSearchTimezone', { searchTerm });
	};

	const gtmEventChangeTimezone = (timezone: string): void => {
		pushTagManagerEvent('bookingChangeTimezone', { timezone });
	};

	const gtmEventSubmissionError = (errorCode: string): void => {
		pushTagManagerEvent('bookingSubmissionError', { errorCode });
	};

	const gtmEventBookAppointment = (appointmentId: string, formFields: FormField[]): void => {
		const hasNotes = formFields.some((field) => field.name === 'notes' && !!field.value);
		const notesRequired = formFields.some((field) => field.name === 'notes' && field.required);
		pushTagManagerEvent('bookingBookAppointment', { appointmentId, hasNotes, notesRequired });
	};

	const gtmEventTimeSlotExpired = (): void => {
		pushTagManagerEvent('bookingTimeSlotExpired');
	};

	const gtmEventDownloadICS = (): void => {
		pushTagManagerEvent('bookingDownloadICS');
	};

	const gtmEventCancelAppointment = (): void => {
		pushTagManagerEvent('bookingCancelAppointment');
	};

	const gtmEventCancelAppointmentError = (): void => {
		pushTagManagerEvent('bookingCancelAppointmentError');
	};

	const dispatchWindowBookedEvent = (eventData: Record<string, unknown>): void => {
		const event = new CustomEvent('mcflyBookingBookedAppointment', { detail: eventData });
		window.dispatchEvent(event);
	};

	return {
		gtmInitializeBookingData,
		gtmEventEditAppointment,
		gtmEventChangeAppointment,
		gtmEventSearchTimezone,
		gtmEventChangeTimezone,
		gtmEventSubmissionError,
		gtmEventBookAppointment,
		gtmEventTimeSlotExpired,
		gtmEventDownloadICS,
		gtmEventCancelAppointment,
		gtmEventCancelAppointmentError,
		dispatchWindowBookedEvent,
	};
};
